package dev.youndie.site.pages.blog

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initAskTheDesktopPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("blog/AskTheDesktop.md", FrontMatterElement.Builder { addScalar("title", "Ask the desktop, don't guess it"); addScalar("searchTitle", "Custom title bar for Compose Desktop on Windows, macOS and Linux"); addScalar("date", "2025-09-14"); addScalar("description", "A custom title bar for Compose Desktop has to know which side the buttons go on. On Linux there is no answer — so appframe asks GNOME directly."); addScalar("library", "appframe"); }))
}

@Page("/blog/ask-the-desktop")
@Layout(".components.layouts.MarkdownLayout")
@Composable
fun AskTheDesktopPage() {
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A custom window frame looks like a small job until you notice that \"where do the window buttons go\" has three different right answers, and one of them is not knowable in advance.")
    }
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/appframe") {
            org.jetbrains.compose.web.dom.Text("appframe")
        }
        org.jetbrains.compose.web.dom.Text(" draws the title bar in Compose and wires up the real window controls underneath. Most of the work is not drawing.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("two-platforms-tell-you") }) {
        org.jetbrains.compose.web.dom.Text("Two platforms tell you")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("macOS puts the traffic lights on the left. Windows puts minimize, maximize and close on the right. Both are stable conventions, so both are a lookup on the host OS.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The details are where it stops being a lookup:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Windows 11 has specific button metrics, including the red hover on close. Get the width wrong and it reads as a knock-off immediately.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("The macOS lights dim when the window loses focus and only reveal their glyphs on hover.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("The green button is ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("not")
            }
            org.jetbrains.compose.web.dom.Text(" maximize. It goes fullscreen, and treating it as maximize is one of the tells that an app was ported rather than written for the platform.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Corners are rounded on macOS and squared off again the moment the window is maximized or fullscreen.")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("linux-does-not") }) {
        org.jetbrains.compose.web.dom.Text("Linux does not")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("There is no Linux convention. GNOME ships with close only, on the right. KDE and XFCE put three on the right. elementary OS puts them on the left, as did the old Unity layout.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("You cannot infer this from the distribution, and you should not try. The desktop already knows, and it will tell you:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-text") }) { org.jetbrains.compose.web.dom.Text("""org.gnome.desktop.wm.preferences button-layout
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("That setting decides both the side and ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("which buttons exist at all")
        }
        org.jetbrains.compose.web.dom.Text(" — which is why a hardcoded three-button layout looks wrong on a stock GNOME desktop rather than merely misplaced. appframe reads it and lays out accordingly, falling back to the right-hand layout when the setting cannot be read.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("This is the whole point of the library, compressed into one sentence: the environment is the source of truth, and asking it is cheaper than modelling it.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("maximize-is-not-resize") }) {
        org.jetbrains.compose.web.dom.Text("Maximize is not resize")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The other thing that separates a real title bar from a drawn one is what maximize does. Setting the window to the screen bounds covers the taskbar and misbehaves across monitors. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("WindowPlacement.Maximized")
        }
        org.jetbrains.compose.web.dom.Text(" hands the decision to the window manager, which knows about docks and about the monitor the window is actually on.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Double-clicking the title bar toggles it, because that is what a title bar does.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-it-deliberately-does-not-have") }) {
        org.jetbrains.compose.web.dom.Text("What it deliberately does not have")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("No icon dependency. Every glyph is drawn with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Canvas")
        }
        org.jetbrains.compose.web.dom.Text(", so adding a window frame does not add a font or an icon pack to your build.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("No theme of its own either — it takes Material 3 colours from whatever ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("MaterialTheme")
        }
        org.jetbrains.compose.web.dom.Text(" it finds itself in, and it is a drop-in replacement for the ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Window")
        }
        org.jetbrains.compose.web.dom.Text(" block you already have.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Source: ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/appframe") {
            org.jetbrains.compose.web.dom.Text("github.com/youndie/appframe")
        }
    }
}
